import { useEffect, useState } from 'react';
import { api, fetchBinaryWithTimeout } from '../api.js';
import { toast } from './Toasts.jsx';
import { useAuth } from '../auth/AuthContext.jsx';
import { AiKeyMissingNotice } from './AiKeyBanner.jsx';
import ConfirmDialog from './ConfirmDialog.jsx';
import { Modal } from './ui';
import AiBusy, { AI_COST } from './AiBusy.jsx';
import { formatDateTime } from '../util/time.js';
import LanguageSelect from './LanguageSelect.jsx';
import { getPreferredLanguage, setPreferredLanguage } from '../util/languages.js';
import { languageDefault, languageReasonText } from '../util/postingLanguageDefault.js';

// T067 + T068 — Tailored CV per recommendation.
//
// Per-rec, Claude rewrites ONLY the Professional Profile section to fit
// THIS job. The operator's active .docx (with a {{SUMMARY}} marker
// placed once) is merged server-side and either downloaded as the
// merged .docx OR converted to PDF via LibreOffice headless — both
// format-preserving against the source .docx.
//
// No bare-PDF builder anymore. Both downloads route through the
// operator's own .docx so the visual result matches their CV exactly.

// Prefer the RFC 5987 `filename*` parameter (carries dashes and aeoa
// intact) and fall back to the ASCII `filename=` the backend always
// sends alongside it.
function filenameFromDisposition(cd, fallback) {
  const star = /filename\*=UTF-8''([^;]+)/i.exec(cd ?? '');
  if (star) {
    try {
      return decodeURIComponent(star[1]);
    } catch {
      // Malformed encoding - fall through to the ASCII form.
    }
  }
  const plain = /filename="([^"]+)"/.exec(cd ?? '');
  return plain ? plain[1] : fallback;
}

// T301/T303/T306 — the two downloads have a real deadline, and the helper that
// gives it to them lives in api.js.
//
// `fetch` has no default timeout and both handlers below clear their button
// state only in `finally`, so a request that never settles left the button
// reading "merging…" or "converting…" forever. That is what the operator hit on
// 2026-09-23 — and the cause was server-side: a wedged LibreOffice that the
// conversion's own 30s timeout could not end (T303), so the request sat until
// Apache's ProxyTimeout 600 gave up.
//
// The ceilings are not performance bounds. The measured server path — load the
// active CV, merge, convert — is 3.8s end to end, and a cold soffice budgets
// 5-10s. They are the line past which "still working" stops being credible.
//
// The helper is in api.js, not here, because that file owns the 401 contract
// (an expired session must announce itself, not just red-toast "unauthorized")
// and because a component module that exports non-components stops being a
// React Fast Refresh boundary.
const DOCX_TIMEOUT_MS = 60_000;
// T385 — 120s, deliberately LONGER than the server's 90s conversion bound.
//
// While they were equal the two deadlines raced, and the client usually won:
// he got the generic "The PDF conversion timed out" instead of the server's
// `pdf_convert_timeout` or `pdf_convert_busy`, which say what actually
// happened. The client abort is the backstop for a server that never answers,
// so it has to sit outside the server's own limit, not on top of it.
const PDF_TIMEOUT_MS = 120_000;

export default function TailoredCvModal({ rec, onClose, onUpdated }) {
  const job = rec?.job;
  const existing = rec?.tailoredCv ?? null;
  // T088 — A/B tailored CV variants. Independent from the single-summary
  // flow above; the operator picks one variant which then becomes the
  // active tailoredCv (so docx merge / PDF download flow unchanged).
  const variantsBlock = rec?.tailoredCvVariants ?? null;
  const { user } = useAuth();
  const hasAnthropicKey = !!user?.connectors?.anthropicKey;

  const [summary, setSummary] = useState(existing?.summary ?? '');
  const [rationale, setRationale] = useState(existing?.rationale ?? '');
  const [notes, setNotes] = useState(existing?.notes ?? '');
  // Output language for the generated summary / variants. Restores an
  // existing draft's language, else the operator's sticky last choice.
  // T469 — the posting's own language outranks the sticky preference.
  // `variantsBlock` counts as an existing draft: it is a choice he made.
  const langDefault = languageDefault({
    existingLanguage: existing?.language || variantsBlock?.language,
    postingLanguage: rec?.job?.postingLanguage,
  });
  const [language, setLanguage] = useState(langDefault.code);
  const changeLanguage = (code) => {
    setLanguage(code);
    setPreferredLanguage(code);
  };
  const [generating, setGenerating] = useState(false);
  const [saving, setSaving] = useState(false);
  const [variants, setVariants] = useState(variantsBlock);
  const [generatingVariants, setGeneratingVariants] = useState(false);

  // T068 — Active CV merge readiness. Set by the active-cv-status probe.
  const [cvStatus, setCvStatus] = useState(null);
  const [cvStatusLoading, setCvStatusLoading] = useState(true);
  const [downloadingDocx, setDownloadingDocx] = useState(false);

  useEffect(() => {
    api.get(`/recommendations/${rec.id}/tailored-cv/active-cv-status`)
      .then(setCvStatus)
      .catch((e) => setCvStatus({ ok: false, reason: 'probe_failed', message: e.body?.message || e.message }))
      .finally(() => setCvStatusLoading(false));
  }, [rec.id]);

  const [confirmDiscard, setConfirmDiscard] = useState(false);
  const [confirmClose, setConfirmClose] = useState(false); // T111 — unsaved-edits guard
  // T133 — one-deep undo: the prior summary the server stashed on regenerate/adopt.
  const [previous, setPrevious] = useState(rec?.tailoredCvPrevious ?? null);
  const [confirmRestore, setConfirmRestore] = useState(false);
  const [restoring, setRestoring] = useState(false);

  // If a new summary lands via regen, sync local state
  useEffect(() => {
    if (existing) {
      setSummary(existing.summary ?? '');
      setRationale(existing.rationale ?? '');
      setNotes(existing.notes ?? '');
    }
  }, [existing]);

  useEffect(() => {
    setVariants(variantsBlock);
  }, [variantsBlock]);

  // T088 — Generate A/B variants. One Claude call, two distinct framings.
  const generateVariants = async () => {
    setGeneratingVariants(true);
    try {
      const r = await api.post(`/recommendations/${rec.id}/cv-variants`, { notes, language });
      setVariants(r.tailoredCvVariants);
      toast('A/B variants drafted', { kind: 'success' });
    } catch (e) {
      const msg = e.body?.error === 'anthropic_unauthorized'
        ? 'Set your Anthropic API key in Settings → Connectors first.'
        : e.body?.message || e.message;
      toast(msg, { kind: 'error' });
    } finally {
      setGeneratingVariants(false);
    }
  };

  // T088 — Adopt one of the variants as the active tailored summary. Two
  // network calls: (a) PATCH the tailoredCv with the chosen summary so the
  // docx merge + PDF download paths work as before, and (b) PATCH the
  // variants block to track which one was selected for the A/B analytics.
  const adoptVariant = async (letter) => {
    if (!variants) return;
    const v = letter === 'A' ? variants.variantA : variants.variantB;
    if (!v?.summary) return;
    try {
      if (existing) {
        // T133 — ONE atomic write: the summary, the variant marker, and the
        // prior-summary snapshot all land in the same save, so a failed second
        // call can't leave summary + selection diverged.
        const r = await api.patch(`/recommendations/${rec.id}/cv-variants/select`, {
          selectedVariant: letter,
          adoptSummary: v.summary,
        });
        const tc = r.tailoredCv;
        if (tc) {
          setSummary(tc.summary);
          onUpdated?.({ tailoredCv: tc, tailoredCvPrevious: r.tailoredCvPrevious ?? null });
        }
        setRationale(v.rationale ?? tc?.rationale ?? rationale);
        setVariants(r.tailoredCvVariants);
        setPrevious(r.tailoredCvPrevious ?? null); // server stashed the prior summary
      } else {
        // No tailoredCv yet — set local state (operator hits Save to persist),
        // and just track the variant choice (single call, no summary write).
        setSummary(v.summary);
        setRationale(v.rationale ?? '');
        const r = await api.patch(`/recommendations/${rec.id}/cv-variants/select`, { selectedVariant: letter });
        setVariants(r.tailoredCvVariants);
      }
      toast(`Variant ${letter} adopted as active`, { kind: 'success' });
    } catch (e) {
      toast(e.body?.message || e.message, { kind: 'error' });
    }
  };

  const dirty = !!existing && summary !== (existing.summary ?? '');

  // T111 — closing with unsaved manual edits (summary) routes through a confirm.
  const attemptClose = () => {
    if (dirty) setConfirmClose(true);
    else onClose?.();
  };
  // Escape is owned by <Modal> (onClose={attemptClose}, suppressed via
  // escapeClosesOnTop while a nested ConfirmDialog is open).

  const generate = async () => {
    setGenerating(true);
    try {
      const r = await api.post(`/recommendations/${rec.id}/tailored-cv`, { notes, language });
      const tc = r.recommendation.tailoredCv;
      setSummary(tc.summary);
      setRationale(tc.rationale);
      setNotes(tc.notes ?? '');
      setPrevious(r.recommendation.tailoredCvPrevious ?? null); // T133 — light the restore chip
      onUpdated?.({ tailoredCv: tc, tailoredCvPrevious: r.recommendation.tailoredCvPrevious ?? null });
      toast('Tailored summary generated', { kind: 'success' });
    } catch (e) {
      const msg = e.body?.error === 'anthropic_unauthorized'
        ? 'Set your Anthropic API key in Settings → Connectors first.'
        : e.body?.message || e.message;
      toast(msg, { kind: 'error' });
    } finally {
      setGenerating(false);
    }
  };

  // T133 — restore the stashed previous summary (one-deep undo; server clears it).
  const restore = async () => {
    setRestoring(true);
    try {
      const r = await api.post(`/recommendations/${rec.id}/tailored-cv/restore-previous`);
      const tc = r.recommendation.tailoredCv;
      setSummary(tc.summary);
      setRationale(tc.rationale ?? '');
      setNotes(tc.notes ?? '');
      setPrevious(null);
      setConfirmRestore(false);
      toast('Restored previous version', { kind: 'success' });
      onUpdated?.({ tailoredCv: tc, tailoredCvPrevious: null });
    } catch (e) {
      toast(e.body?.message || e.message || 'Restore failed', { kind: 'error' });
    } finally {
      setRestoring(false);
    }
  };

  const saveEdits = async () => {
    setSaving(true);
    try {
      const r = await api.patch(`/recommendations/${rec.id}/tailored-cv`, { summary, notes });
      const tc = r.recommendation.tailoredCv;
      onUpdated?.({ tailoredCv: tc, tailoredCvPrevious: previous ?? null }); // T133 — a manual edit doesn't touch the undo buffer
      toast('Edits saved', { kind: 'success' });
    } catch (e) {
      toast(e.body?.message || e.message, { kind: 'error' });
    } finally {
      setSaving(false);
    }
  };

  const discard = async () => {
    if (!existing) return;
    try {
      await api.delete(`/recommendations/${rec.id}/tailored-cv`);
      setSummary('');
      setRationale('');
      setNotes('');
      setPrevious(null); // T133 — discarding the draft discards its undo buffer
      onUpdated?.({ tailoredCv: null, tailoredCvPrevious: null });
      toast('Tailored CV discarded', { kind: 'success' });
    } catch (e) {
      toast(e.body?.message || e.message, { kind: 'error' });
    }
  };

  // T068 — Download the active CV .docx with {{SUMMARY}} replaced by the
  // tailored summary. Format-preserving — uses the operator's own .docx
  // template. Falls back to the generated PDF if the active CV is not
  // ready (no .docx / no marker).
  const downloadActiveCvDocx = async () => {
    if (!summary) {
      toast('Generate the tailored summary first.', { kind: 'info' });
      return;
    }
    setDownloadingDocx(true);
    try {
      const res = await fetchBinaryWithTimeout(
        `/api/recommendations/${rec.id}/tailored-cv/docx`,
        { timeoutMs: DOCX_TIMEOUT_MS, what: 'The .docx merge' },
      );
      if (!res.ok) {
        const body = await res.json().catch(() => ({}));
        throw new Error(body.message || body.error || `http_${res.status}`);
      }
      const blob = await res.blob();
      const cd = res.headers.get('Content-Disposition') ?? '';
      const filename = filenameFromDisposition(cd, 'tailored-cv.docx');
      const url = URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = filename;
      document.body.appendChild(a);
      a.click();
      document.body.removeChild(a);
      URL.revokeObjectURL(url);
      toast('Tailored .docx downloaded', { kind: 'success' });
    } catch (e) {
      toast(e.message, { kind: 'error' });
    } finally {
      setDownloadingDocx(false);
    }
  };

  // T068b — Download tailored CV as PDF. Backend merges the .docx + the
  // tailored summary, then runs LibreOffice headless to convert .docx
  // → PDF. Format-preserved (same fonts/layout as the source).
  // 5-10s round-trip (cold soffice). Same readiness rules as the .docx
  // path: needs .docx active CV with {{SUMMARY}} marker.
  const [downloadingPdf, setDownloadingPdf] = useState(false);
  const downloadTailoredPdf = async () => {
    if (!summary) {
      toast('Generate the tailored summary first.', { kind: 'info' });
      return;
    }
    setDownloadingPdf(true);
    try {
      const res = await fetchBinaryWithTimeout(
        `/api/recommendations/${rec.id}/tailored-cv/pdf`,
        { timeoutMs: PDF_TIMEOUT_MS, what: 'The PDF conversion' },
      );
      if (!res.ok) {
        const body = await res.json().catch(() => ({}));
        throw new Error(body.message || body.error || `http_${res.status}`);
      }
      const blob = await res.blob();
      const cd = res.headers.get('Content-Disposition') ?? '';
      const filename = filenameFromDisposition(cd, 'tailored-cv.pdf');
      const url = URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = filename;
      document.body.appendChild(a);
      a.click();
      document.body.removeChild(a);
      URL.revokeObjectURL(url);
      toast('Tailored PDF downloaded', { kind: 'success' });
    } catch (e) {
      toast(e.message, { kind: 'error' });
    } finally {
      setDownloadingPdf(false);
    }
  };

  return (
    <>
    {/* T131-fu — <Modal> (bare, size lg, scroll). Nested ConfirmDialogs are
        SIBLINGS below; Modal's Escape is suppressed while one is open. */}
    <Modal
      role="dialog"
      size="lg"
      scroll
      labelledById="tailored-cv-title"
      onClose={attemptClose}
      escapeClosesOnTop={!confirmDiscard && !confirmClose && !confirmRestore}
    >
        <header className="flex items-start justify-between gap-3 border-b border-slate-800 p-4">
          <div className="min-w-0">
            <p className="text-[10px] uppercase tracking-wide text-brand-500">Tailored CV</p>
            <h2 id="tailored-cv-title" className="mt-0.5 truncate text-base font-semibold text-slate-100">
              {job?.title ?? 'Job'}
              <span className="ml-2 font-normal text-slate-400">@ {job?.company ?? '?'}</span>
            </h2>
            <p className="mt-0.5 text-xs text-slate-500">
              Rewrites ONLY the Professional Profile. Skills, experience, certifications stay as in your saved profile.
              {existing && (
                <>
                  {' · '}generation {existing.generationCount}
                  {existing.generatedAt && ` · ${formatDateTime(existing.generatedAt)}`}
                </>
              )}
            </p>
          </div>
          <button type="button" onClick={attemptClose} aria-label="Close" className="rounded-md border border-slate-700 px-2 py-1 text-xs text-slate-300 hover:border-slate-500 hover:text-slate-200">✕</button>
        </header>

        {/* Output-language selector — governs the A/B variants, the single
            summary, and regenerate. Sits outside the scroll area so its
            dropdown isn't clipped. */}
        <div className="relative z-30 flex items-center gap-3 border-b border-slate-800 bg-slate-900/40 px-4 py-2.5">
          <span className="text-[10px] uppercase tracking-wide text-slate-400">Language</span>
          <LanguageSelect value={language} onChange={changeLanguage} id="tailored-cv-language" />
          {/* T469 — same as the cover letter: while it is still the
              default, the label says where the default came from. */}
          <span className="hidden text-xs text-slate-500 sm:inline">
            {language === langDefault.code
              ? languageReasonText(langDefault.reason, langDefault.code)
              : 'the summary will be written in this language'}
          </span>
        </div>

        <div className="flex-1 overflow-y-auto p-4">
          {/* T088 — A/B variants block. Available in both initial + after-existing
              states. Operator can pick a variant which becomes the active summary. */}
          <VariantsBlock
            variants={variants}
            generating={generatingVariants}
            notes={notes}
            onNotesChange={setNotes}
            onGenerate={generateVariants}
            onAdopt={adoptVariant}
            hasAnthropicKey={hasAnthropicKey}
          />

          {!existing && !summary ? (
            <div className="space-y-4">
              <p className="text-sm text-slate-400">
                Or generate a single summary for <strong className="text-slate-200">{job?.title}</strong> at{' '}
                <strong className="text-slate-200">{job?.company}</strong>. Cost: {AI_COST.tailoredCv.cost} per generation.
              </p>
              <div>
                <label className="text-[10px] uppercase tracking-wide text-slate-500">Framing notes (optional)</label>
                <textarea
                  value={notes}
                  onChange={(e) => setNotes(e.target.value)}
                  rows={3}
                  placeholder='e.g. "Lead with the Acme failover work; this team is platform-focused."'
                  className="mt-1 w-full rounded-md border border-slate-700 bg-slate-950/40 px-3 py-2 text-sm text-slate-100 focus:border-brand-500 focus:outline-none"
                />
              </div>
              {hasAnthropicKey ? (
                <button
                  type="button"
                  onClick={generate}
                  disabled={generating}
                  className="rounded-md bg-brand-600 px-4 py-2 text-sm font-semibold text-white transition hover:bg-brand-500 disabled:opacity-50"
                >
                  {generating ? <AiBusy feature="tailoredCv" compact /> : '✨ Generate single summary'}
                </button>
              ) : (
                <AiKeyMissingNotice feature="Tailored CVs" />
              )}
            </div>
          ) : (
            <div className="space-y-4">
              <div>
                <label className="text-[10px] uppercase tracking-wide text-slate-400">Tailored Professional Profile</label>
                <textarea
                  value={summary}
                  onChange={(e) => setSummary(e.target.value)}
                  rows={8}
                  className="mt-1 w-full rounded-md border border-slate-700 bg-slate-950/40 px-3 py-2 text-sm leading-relaxed text-slate-100 focus:border-brand-500 focus:outline-none"
                />
                <p className="mt-1 text-xs text-slate-500">
                  {summary.length} characters · ~{Math.round(summary.split(/\s+/).filter(Boolean).length)} words
                </p>
              </div>

              {rationale && (
                <div className="rounded-lg border border-amber-700 bg-amber-950/15 p-3">
                  <div className="text-[10px] uppercase tracking-wide text-amber-400">Framing rationale (not in CV)</div>
                  <p className="mt-1 text-xs text-amber-200">{rationale}</p>
                </div>
              )}

              <details className="rounded-lg border border-slate-800 bg-slate-900/40 p-3">
                <summary className="cursor-pointer text-xs text-slate-400 hover:text-slate-200">Notes for next regeneration</summary>
                <textarea
                  value={notes}
                  onChange={(e) => setNotes(e.target.value)}
                  rows={3}
                  placeholder='e.g. "Make it shorter, drop the Terraform mention."'
                  className="mt-2 w-full rounded-md border border-slate-700 bg-slate-950/40 px-3 py-2 text-sm text-slate-100 focus:border-brand-500 focus:outline-none"
                />
              </details>

              {/* T068 — Active CV merge readiness (primary path) */}
              <ActiveCvStatusBanner status={cvStatus} loading={cvStatusLoading} />

            </div>
          )}
        </div>

        {/* T133 — previous-version chip (icon + text, never colour alone) */}
        {previous && (
          <div className="flex flex-wrap items-center gap-2 border-t border-amber-800/60 bg-amber-950/20 px-3 py-2 text-xs text-amber-200">
            <span aria-hidden="true">↩</span>
            <span>
              Previous version available
              {previous.generatedAt && ` · ${formatDateTime(previous.generatedAt)}`}
            </span>
            <button
              type="button"
              onClick={() => setConfirmRestore(true)}
              disabled={restoring}
              className="ml-auto rounded border border-amber-600 px-2 py-0.5 text-amber-200 transition hover:bg-amber-600/10 disabled:opacity-50"
            >
              Restore previous
            </button>
          </div>
        )}

        {(existing || summary) && (
          <footer className="flex flex-wrap items-center gap-2 border-t border-slate-800 bg-slate-900/60 p-3">
            {hasAnthropicKey && (
              <button type="button" onClick={generate} disabled={generating || saving}
                className="rounded-md border border-brand-600 px-3 py-1.5 text-xs text-brand-500 hover:bg-brand-600/10 disabled:opacity-50"
                title="Re-run with current notes — overwrites the summary">
                {generating ? <AiBusy feature="tailoredCv" compact label="Regenerating" /> : '✨ Regenerate'}
              </button>
            )}
            <button type="button" onClick={saveEdits} disabled={!dirty || saving || generating}
              className="rounded-md bg-brand-600 px-3 py-1.5 text-xs font-medium text-white transition hover:bg-brand-500 disabled:opacity-50">
              {saving ? 'saving…' : 'Save edits'}
            </button>
            <button
              type="button"
              onClick={downloadActiveCvDocx}
              disabled={downloadingDocx || !cvStatus?.ok || !summary}
              className="rounded-md bg-brand-600 px-3 py-1.5 text-xs font-medium text-white transition hover:bg-brand-500 disabled:bg-slate-700 disabled:text-slate-400 disabled:cursor-not-allowed"
              title={cvStatus?.ok ? 'Merge tailored summary into your active CV .docx (format-preserved)' : cvStatus?.message ?? 'Active CV not ready'}
            >
              {downloadingDocx ? 'merging…' : '📥 Download tailored .docx'}
            </button>
            <button
              type="button"
              onClick={downloadTailoredPdf}
              disabled={downloadingPdf || !cvStatus?.ok || !summary}
              className="rounded-md border border-brand-600 px-3 py-1.5 text-xs text-brand-500 hover:bg-brand-600/10 disabled:border-slate-700 disabled:text-slate-500 disabled:cursor-not-allowed"
              title={cvStatus?.ok ? 'Merge into your .docx + convert to PDF (format-preserved via LibreOffice)' : cvStatus?.message ?? 'Active CV not ready'}>
              {downloadingPdf ? 'converting…' : '📄 Download tailored PDF'}
            </button>
            <button type="button" onClick={() => setConfirmDiscard(true)}
              className="ml-auto rounded-md border border-slate-700 px-3 py-1.5 text-xs text-slate-500 hover:border-red-700 hover:text-red-400">
              Discard
            </button>
          </footer>
        )}
    </Modal>

      {confirmDiscard && (
        <ConfirmDialog
          title="Discard this tailored summary?"
          message="Re-generating is cheap, but any manual edits you made will be lost."
          confirmLabel="Discard"
          busyLabel="discarding…"
          onCancel={() => setConfirmDiscard(false)}
          onConfirm={async () => {
            await discard();
            setConfirmDiscard(false);
          }}
        />
      )}

      {confirmClose && (
        <ConfirmDialog
          title="Discard unsaved edits?"
          message="You've edited the tailored summary but haven't saved. Closing now loses those changes."
          confirmLabel="Discard & close"
          cancelLabel="Keep editing"
          busyLabel="closing…"
          onCancel={() => setConfirmClose(false)}
          onConfirm={() => {
            setConfirmClose(false);
            onClose?.();
          }}
        />
      )}

      {confirmRestore && (
        <ConfirmDialog
          title="Restore the previous version?"
          message="Replaces the current summary with the version from before the last regeneration. The current one is discarded."
          confirmLabel="Restore previous"
          cancelLabel="Keep current"
          tone="default"
          busyLabel="restoring…"
          onCancel={() => setConfirmRestore(false)}
          onConfirm={restore}
        />
      )}
    </>
  );
}

// ─── Active CV merge readiness banner ────────────────────────────────────────
// Shows the operator at a glance whether their active CV is ready for
// in-place merging (must be .docx + contain the {{SUMMARY}} marker), and
// when not, gives them the exact one-time steps to fix it.
// T088 — A/B variants block. Generates two distinct summaries in one
// Claude call; operator picks one to adopt as the active tailored summary.
// The pick is recorded for A/B analytics.
function VariantsBlock({ variants, generating, notes, onNotesChange, onGenerate, onAdopt, hasAnthropicKey }) {
  if (!variants && !generating) {
    return (
      <div className="mb-4 rounded-lg border border-brand-700 bg-brand-600/5 p-3">
        <div className="flex flex-wrap items-baseline justify-between gap-2">
          <p className="text-[10px] uppercase tracking-widest text-brand-400">
            ✨ A/B variants (recommended)
          </p>
          <span className="text-xs text-slate-400">{AI_COST.cvVariants.cost} per generation</span>
        </div>
        <p className="mt-1 text-xs text-slate-300">
          Generate two distinct framings of the Professional Profile (e.g. leadership-led vs technical-depth-led). Pick which one converts better; the system tracks response rates.
        </p>
        <details className="mt-2">
          <summary className="cursor-pointer text-[11px] text-slate-500">Framing notes (optional)</summary>
          <textarea
            value={notes}
            onChange={(e) => onNotesChange(e.target.value)}
            rows={2}
            placeholder='e.g. "One leadership-led, one IC-led."'
            className="mt-1 w-full rounded border border-slate-700 bg-slate-950/40 px-2 py-1 text-xs text-slate-100"
          />
        </details>
        {hasAnthropicKey ? (
          <button
            type="button"
            onClick={onGenerate}
            className="mt-2 rounded-md bg-brand-600 px-3 py-1.5 text-xs font-medium text-white hover:bg-brand-500"
          >
            ✨ Generate A/B variants
          </button>
        ) : (
          <div className="mt-2">
            <AiKeyMissingNotice feature="A/B variants" />
          </div>
        )}
      </div>
    );
  }

  if (generating) {
    return (
      <div className="mb-4 rounded-lg border border-brand-700 bg-brand-600/5 p-3">
        <AiBusy feature="cvVariants" />
      </div>
    );
  }

  const selected = variants?.selectedVariant ?? null;

  return (
    <div className="mb-4 rounded-lg border border-brand-700 bg-brand-600/5 p-3">
      <div className="flex flex-wrap items-baseline justify-between gap-2">
        <p className="text-[10px] uppercase tracking-widest text-brand-400">A/B variants</p>
        <button
          type="button"
          onClick={onGenerate}
          className="rounded border border-slate-700 px-2 py-0.5 text-[10px] text-slate-400 hover:border-brand-600 hover:text-brand-400"
        >
          ↻ Regenerate
        </button>
      </div>
      <div className="mt-2 grid gap-3 sm:grid-cols-2">
        {['A', 'B'].map((letter) => {
          const v = letter === 'A' ? variants.variantA : variants.variantB;
          if (!v) return null;
          const isSelected = selected === letter;
          return (
            <div
              key={letter}
              className={
                'flex flex-col rounded-lg border p-3 ' +
                (isSelected ? 'border-emerald-700 bg-emerald-950/20' : 'border-slate-800 bg-slate-950/40')
              }
            >
              <div className="flex items-baseline justify-between gap-2">
                <p className={'text-[10px] uppercase tracking-widest ' + (isSelected ? 'text-emerald-400' : 'text-slate-500')}>
                  Variant {letter}{isSelected ? ' · adopted' : ''}
                </p>
              </div>
              <p className="mt-0.5 text-xs font-medium text-slate-200">{v.label}</p>
              <p className="mt-2 flex-1 text-sm leading-relaxed text-slate-100">{v.summary}</p>
              {v.rationale && (
                <p className="mt-2 text-[11px] italic text-slate-500">{v.rationale}</p>
              )}
              <button
                type="button"
                onClick={() => onAdopt(letter)}
                disabled={isSelected}
                className={
                  'mt-3 rounded-md px-3 py-1 text-xs font-medium ' +
                  (isSelected
                    ? 'bg-emerald-900/30 text-emerald-300 cursor-not-allowed'
                    : 'bg-brand-600 text-white hover:bg-brand-500')
                }
              >
                {isSelected ? '✓ Adopted' : 'Use this one →'}
              </button>
            </div>
          );
        })}
      </div>
      {selected && (
        <p className="mt-2 text-xs text-slate-500">
          Variant {selected} is now the active tailored summary below. Outcomes are tracked for A/B analytics.
        </p>
      )}
    </div>
  );
}

function ActiveCvStatusBanner({ status, loading }) {
  if (loading) {
    return (
      <div className="rounded-lg border border-slate-800 bg-slate-950/30 p-3 text-xs text-slate-400">
        Checking active CV…
      </div>
    );
  }
  if (!status) return null;
  if (status.ok) {
    return (
      <div className="rounded-lg border border-emerald-800 bg-emerald-950/20 p-3">
        <p className="text-[10px] uppercase tracking-wide text-emerald-400">Active CV ready ✓</p>
        <p className="mt-1 text-xs text-emerald-200">
          <code className="text-emerald-100">{status.filename}</code> contains the {'{{SUMMARY}}'} marker. Click <strong>Download tailored .docx</strong> to get a format-preserved copy with the summary above swapped in.
        </p>
      </div>
    );
  }
  // Not ready — show actionable guidance per reason.
  const heading =
    status.reason === 'no_active_cv'
      ? 'No CV uploaded yet'
      : status.reason === 'unsupported_format'
        ? 'Active CV format unsupported'
        : status.reason === 'marker_missing'
          ? 'Active CV missing the {{SUMMARY}} marker'
          : 'Active CV not ready';
  return (
    <div className="rounded-lg border border-amber-700 bg-amber-950/15 p-3">
      <p className="text-[10px] uppercase tracking-wide text-amber-400">⚠ {heading}</p>
      <p className="mt-1 text-xs text-amber-200">{status.message}</p>
      {status.reason === 'marker_missing' && (
        <ol className="mt-2 list-decimal space-y-1 pl-5 text-[11px] text-amber-100/90">
          <li>Open your CV in Word / LibreOffice (either .doc or .docx works — server handles .doc conversion losslessly via LibreOffice).</li>
          <li>In the Professional Profile section, <strong>replace the intro paragraph</strong> with literally <code>{'{{SUMMARY}}'}</code> (with the double curly braces).</li>
          <li>Save (keep the same format — .doc stays .doc, .docx stays .docx).</li>
          <li>Go to <strong>Documents</strong>, upload as type "CV", then click <strong>make active</strong> on that row.</li>
          <li>Come back here — banner turns green, downloads work.</li>
        </ol>
      )}
      {status.reason === 'unsupported_format' && (
        <p className="mt-2 text-[11px] text-amber-100/90">
          Supported: <strong>.doc</strong> (Europass-style binary) or <strong>.docx</strong> (modern Word). Convert your CV to one of these and re-upload.
        </p>
      )}
    </div>
  );
}

